import { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Phone, ArrowRight, CheckCircle2, Paintbrush, Hammer, Layers, Home as HomeIcon, Shield, Award } from 'lucide-react';

export const CITIES = {
  vernon: { name: 'Vernon', lat: 50.267, lng: -119.272, blurb: 'Headquartered right here in Vernon, we are the local painting and construction team your neighbours already trust. From Okanagan Landing to East Hill, we deliver flawless finishes on homes and commercial spaces.' },
  kelowna: { name: 'Kelowna', lat: 49.888, lng: -119.496, blurb: 'Serving Kelowna and the Central Okanagan with professional painting and full construction services - from downtown commercial spaces to lakeside homes in the Mission and Glenmore.' },
  kamloops: { name: 'Kamloops', lat: 50.676, lng: -120.341, blurb: 'Our crews regularly work across Kamloops, delivering interior and exterior painting, framing, drywall and complete builds for homes and businesses throughout the Thompson Valley.' },
  armstrong: { name: 'Armstrong', lat: 50.448, lng: -119.196, blurb: 'Just minutes up the highway from our Vernon shop, Armstrong homeowners and businesses count on us for fast, high-quality painting, siding and renovation work.' },
  enderby: { name: 'Enderby', lat: 50.551, lng: -119.14, blurb: 'From riverside homes to farm buildings, we bring professional painting and construction craftsmanship to Enderby and the surrounding Shuswap River valley.' },
  sicamous: { name: 'Sicamous', lat: 50.836, lng: -118.986, blurb: 'We are actively building in Sicamous - including the FOX Block Townhouses and Edgewater Building projects - and offer complete painting and construction services throughout the Shuswap.' },
  revelstoke: { name: 'Revelstoke', lat: 50.998, lng: -118.196, blurb: 'Mountain weather is tough on paint and siding. Our team serves Revelstoke with durable exterior painting, siding and full construction services built for the climate.' },
};

const AREA_SERVICES = [
  { icon: Paintbrush, title: 'Interior & Exterior Painting', desc: 'Premium paints, meticulous prep and clean lines for homes and businesses.' },
  { icon: Hammer, title: 'Framing & Structural Work', desc: 'Expert structural framing for residential and commercial projects.' },
  { icon: Layers, title: 'Drywall & Taping', desc: 'Smooth, flawless walls ready for a perfect coat of paint.' },
  { icon: HomeIcon, title: 'Siding, Flooring & Finishing', desc: 'Complete interior and exterior finishing from start to move-in.' },
];

const PROJECT_PHOTOS = [
  'https://customer-assets-v7afamib.emergentagent.net/job_05714f6e-3774-4985-88d6-8908b452b8f7/artifacts/d7qp46h7_IMG_8178.webp',
  'https://customer-assets.emergentagent.com/job_05714f6e-3774-4985-88d6-8908b452b8f7/artifacts/z7ytpe7v_IMG_6805.jpg',
  'https://customer-assets-v7afamib.emergentagent.net/job_05714f6e-3774-4985-88d6-8908b452b8f7/artifacts/iy45swsg_IMG_8180.webp',
];

export default function ServiceArea() {
  const { citySlug } = useParams();
  const city = CITIES[citySlug];
  const title = city ? `Painting Company in ${city.name}, BC | S&S Group Services` : '';

  useEffect(() => {
    if (title) document.title = title;
  }, [title]);

  if (!city) return <Navigate to="/" replace />;

  const url = `https://www.ssgroupservices.ca/painting-company/${citySlug}`;
  const description = `Top-rated painting & construction company serving ${city.name}, BC. Interior & exterior painting, framing, drywall, siding & finishing. Licensed & insured. Free quotes - call (236) 338-0659.`;
  const otherCities = Object.entries(CITIES).filter(([slug]) => slug !== citySlug);

  return (
    <div data-testid="service-area-page">
      <meta name="description" content={description} />
      <meta name="keywords" content={`painting company ${city.name}, painters ${city.name} BC, house painting ${city.name}, commercial painting ${city.name}, construction company ${city.name} BC, contractor ${city.name}, drywall ${city.name}, siding ${city.name}`} />
      <link rel="canonical" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={PROJECT_PHOTOS[0]} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="geo.region" content="CA-BC" />
      <meta name="geo.placename" content={city.name} />
      <meta name="geo.position" content={`${city.lat};${city.lng}`} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "GeneralContractor",
        "name": "S&S Group Services",
        "url": url,
        "image": PROJECT_PHOTOS[0],
        "description": `Painting and construction company serving ${city.name}, BC and nearby areas.`,
        "telephone": "+1-236-338-0659",
        "email": "info@ssgroupservices.ca",
        "address": { "@type": "PostalAddress", "streetAddress": "1605 34 ST", "addressLocality": "Vernon", "addressRegion": "BC", "postalCode": "V1T 5V4", "addressCountry": "CA" },
        "areaServed": { "@type": "City", "name": city.name, "geo": { "@type": "GeoCoordinates", "latitude": city.lat, "longitude": city.lng } },
        "serviceType": ["Interior Painting", "Exterior Painting", "Framing", "Drywall Installation", "Siding", "Flooring", "Interior Finishing"],
        "priceRange": "$$"
      }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.ssgroupservices.ca/" },
          { "@type": "ListItem", "position": 2, "name": `Painting Company in ${city.name}`, "item": url }
        ]
      }) }} />

      {/* Hero */}
      <section className="relative py-24 bg-slate-900" data-testid="area-hero">
        <div className="absolute inset-0 grid-pattern" />
        <div className="container relative z-10">
          <div className="max-w-3xl">
            <span className="caption text-blue-400">Serving {city.name} & Nearby Areas</span>
            <h1 className="heading-xl text-white mt-3 mb-6" data-testid="area-heading">
              Painting & Construction Company in {city.name}, BC
            </h1>
            <p className="text-slate-300 text-lg mb-8">{city.blurb}</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <Button size="lg" className="btn-accent text-base px-8" data-testid="area-quote-btn">
                  Get a Free Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href="tel:+12363380659">
                <Button size="lg" variant="outline" className="btn-white text-base px-8" data-testid="area-call-btn">
                  <Phone className="w-5 h-5 mr-2" />
                  (236) 338-0659
                </Button>
              </a>
            </div>
            <div className="flex flex-wrap gap-4 mt-10">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm">
                <Shield className="w-5 h-5 text-blue-400" />
                <span className="text-white text-sm font-medium">Licensed & Insured</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm">
                <Award className="w-5 h-5 text-blue-400" />
                <span className="text-white text-sm font-medium">15+ Years Experience</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm">
                <CheckCircle2 className="w-5 h-5 text-blue-400" />
                <span className="text-white text-sm font-medium">Locally Owned & Operated</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding bg-white" data-testid="area-services">
        <div className="container">
          <div className="text-center mb-16">
            <span className="caption text-blue-600">What We Do in {city.name}</span>
            <h2 className="heading-lg text-slate-900 mt-3 mb-4">Painting & Construction Services in {city.name}</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              From a fresh coat of paint to complete builds, S&S Group Services delivers licensed, insured craftsmanship to {city.name} homeowners, builders and businesses.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {AREA_SERVICES.map((s) => (
              <div key={s.title} className="service-card card-hover p-6 border border-slate-100" data-testid={`area-service-${s.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <div className="w-12 h-12 bg-blue-600 flex items-center justify-center mb-4">
                  <s.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>{s.title}</h3>
                <p className="text-slate-600 text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Work */}
      <section className="section-padding bg-slate-50" data-testid="area-work">
        <div className="container">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
            <div>
              <span className="caption text-blue-600">Our Work</span>
              <h2 className="heading-lg text-slate-900 mt-3">Recent Projects Near {city.name}</h2>
            </div>
            <Link to="/gallery" className="mt-4 md:mt-0">
              <Button variant="outline" className="btn-outline" data-testid="area-gallery-btn">
                View Full Gallery
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PROJECT_PHOTOS.map((img, i) => (
              <Link to="/gallery" key={i} className="gallery-item" data-testid={`area-photo-${i}`}>
                <img src={img} alt={`Painting and construction project near ${city.name} BC by S&S Group Services`} loading="lazy" className="h-64 w-full object-cover" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Other areas */}
      <section className="py-12 bg-white border-t border-slate-100" data-testid="area-other-cities">
        <div className="container">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-wider mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>Also Serving Nearby Areas</h2>
          <div className="flex flex-wrap gap-3">
            {otherCities.map(([slug, c]) => (
              <Link key={slug} to={`/painting-company/${slug}`} className="px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-900 hover:text-white transition-colors text-sm font-semibold" data-testid={`area-link-${slug}`}>
                Painting Company in {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-blue-600" data-testid="area-cta">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="heading-lg text-white mb-6">Need Painters or Contractors in {city.name}?</h2>
            <p className="text-blue-100 text-lg mb-8">
              Get a free, no-obligation quote today. We answer within 24 hours.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button size="lg" className="btn-white" data-testid="area-cta-quote-btn">Get a Free Quote</Button>
              </Link>
              <a href="tel:+12363380659">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600" data-testid="area-cta-call-btn">
                  <Phone className="w-5 h-5 mr-2" />
                  (236) 338-0659
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
